import { SWIPE, HAPTICS } from './constants'

type Zone = 'none' | 'basket' | 'delete'
type Drag = {
  id: string
  x0: number; lastX: number; lastT: number; v: number
  width: number
  base: number;      // 0, or -revealPx when the drag started on an open row
  dx: number
  moved: boolean
  zone: Zone
  closing: boolean;  // this touch closed another open row; it must not count as a tap
}
export type GestureState = { open: string | null; drag: Drag | null }

export type GestureEvent =
  | { type: 'down'; id: string; x: number; t: number; width: number }
  | { type: 'move'; x: number; t: number }
  | { type: 'up' }
  | { type: 'cancel' }
  | { type: 'close' }; // tap outside, scroll start, opening a sheet…

export type GestureEffect =
  | { type: 'haptic'; pattern: number | readonly number[] }
  | { type: 'toBasket'; id: string }
  | { type: 'delete'; id: string }   // caller removes optimistically and shows Undo
  | { type: 'edit'; id: string }
  | { type: 'settle'; id: string; to: number }; // animate row to x (0 = closed, -revealPx = open)

export const initialGesture: GestureState = { open: null, drag: null }

export const fullDeletePx = (width: number) => Math.max(SWIPE.fullDeleteMinPx, width * SWIPE.fullDeleteRatio)

const zoneFor = (dx: number, width: number): Zone =>
  dx > SWIPE.armPx ? 'basket' : dx <= -fullDeletePx(width) ? 'delete' : 'none'

/** Pure reducer: pointer events in, effects out. No DOM, no timers. */
export function step(state: GestureState, e: GestureEvent): { state: GestureState; effects: GestureEffect[] } {
  switch (e.type) {
    case 'down': {
      const effects: GestureEffect[] = []
      let open = state.open
      const closing = open !== null && open !== e.id
      if (closing) { effects.push({ type: 'settle', id: open!, to: 0 }); open = null; }
      const base = open === e.id ? -SWIPE.revealPx : 0
      const drag: Drag = { id: e.id, x0: e.x, lastX: e.x, lastT: e.t, v: 0, width: e.width, base, dx: base, moved: false, zone: 'none', closing }
      return { state: { open, drag }, effects }
    }

    case 'move': {
      const d = state.drag
      if (!d) return { state, effects: [] }
      const raw = e.x - d.x0
      if (!d.moved && Math.abs(raw) < SWIPE.slopPx) return { state, effects: [] }
      const v = (e.x - d.lastX) / Math.max(1, e.t - d.lastT)
      const dx = Math.max(-d.width, d.base + raw)
      const zone = zoneFor(dx, d.width)
      const effects: GestureEffect[] = zone !== 'none' && zone !== d.zone ? [{ type: 'haptic', pattern: HAPTICS.arm }] : []
      return { state: { ...state, drag: { ...d, dx, zone, v, lastX: e.x, lastT: e.t, moved: true } }, effects }
    }

    case 'up': {
      const d = state.drag
      if (!d) return { state, effects: [] }
      const done = (open: string | null, effects: GestureEffect[]) => ({ state: { open, drag: null }, effects })

      if (!d.moved) {
        if (d.base < 0) return done(null, [{ type: 'settle', id: d.id, to: 0 }]); // tap on open row closes it
        if (d.closing) return done(null, [])
        return done(null, [{ type: 'edit', id: d.id }])
      }
      const rightFlick = d.base === 0 && d.v >= SWIPE.flickVelocityPxPerMs && d.dx >= SWIPE.flickMinPx
      if (d.zone === 'basket' || rightFlick) return done(null, [{ type: 'haptic', pattern: HAPTICS.drop }, { type: 'toBasket', id: d.id }])
      if (d.zone === 'delete') return done(null, [{ type: 'haptic', pattern: HAPTICS.remove }, { type: 'delete', id: d.id }])

      const leftFlick = d.v <= -SWIPE.flickVelocityPxPerMs && d.dx < -SWIPE.slopPx
      if (d.dx <= -SWIPE.revealPx / 2 || leftFlick) {
        return done(d.id, [{ type: 'haptic', pattern: HAPTICS.reveal }, { type: 'settle', id: d.id, to: -SWIPE.revealPx }])
      }
      return done(null, [{ type: 'settle', id: d.id, to: 0 }])
    }

    case 'cancel': {
      const d = state.drag
      if (!d) return { state, effects: [] }
      return { state: { open: d.base < 0 ? d.id : state.open, drag: null }, effects: [{ type: 'settle', id: d.id, to: d.base }] }
    }

    case 'close':
      return state.open
        ? { state: { open: null, drag: null }, effects: [{ type: 'settle', id: state.open, to: 0 }] }
        : { state, effects: [] }
  }
}

/** What a row should look like right now. Colors and shadows are the host's job. */
export function rowVisual(state: GestureState, id: string) {
  const d = state.drag && state.drag.id === id && state.drag.moved ? state.drag : null
  const dx = d ? d.dx : state.open === id ? -SWIPE.revealPx : 0
  const width = d?.width ?? Infinity
  const armed = dx > SWIPE.armPx
  const deleteArmed = d ? dx <= -fullDeletePx(width) : false
  return {
    dx,
    dragging: d !== null,
    rotateDeg: 0, // no tilt: rows translate only
    basketOpacity: dx > 0 ? Math.min(1, dx / SWIPE.armPx) : 0,
    armed,
    label: armed ? 'Release' : 'Basket',
    deleteWidth: dx < 0 ? -dx : 0,
    deleteArmed,
    deleteLabel: deleteArmed ? 'Release' : 'Delete',
    deleteButtonFocusable: state.open === id,
  }
}
